/**
 * @fileoverview Movement animation manager for moving shapes theme.
 * Handles autonomous shape movement with collision avoidance.
 * Implements IUpdatable interface for GameLoop integration.
 */

import Konva from 'konva';
import { CONFIG } from '../core/GameConfig.js';
import { IUpdatable } from '../core/GameLoop.js';
import { CustomNode } from '../konva-extensions.js';

/**
 * Manages movement animations for draggable shapes.
 * Handles continuous random movement with collision detection.
 * Uses Konva.Tween for smooth animations independent of GameLoop.
 */
export class MovementAnimationManager implements IUpdatable {
    private targets: CustomNode[];
    private draggables: CustomNode[];
    
    /**
     * @param {Konva.Node[]} targets - Array of target shapes to avoid
     * @param {Konva.Node[]} draggables - Array of draggable shapes to animate
     */
    constructor(targets: CustomNode[], draggables: CustomNode[]) {
        this.targets = targets;
        this.draggables = draggables;
    }
    
    /**
     * Start movement animation for all draggable shapes.
     * Each shape moves in a random pattern within the canvas boundaries.
     */
    startAll() {
        this.draggables.forEach((draggable: CustomNode) => {
            // Skip if already matched
            if (draggable.isMatched) return;
            
            // Start animation for this individual shape
            this.animateSingle(draggable);
        });
    }
    
    /**
     * Animate a single shape to a random position.
     * @param {Konva.Node} draggable - The draggable shape to animate
     */
    animateSingle(draggable: CustomNode) {
        // Skip if already matched or being dragged or manually stopped
        if (draggable.isMatched || draggable.isDragging() || draggable.animationStopped) return;
        
        // Clean up any existing animation first
        if (draggable.movementAnim) {
            draggable.movementAnim.destroy();
            // draggable.movementAnim = null;
        }
        
        const margin = CONFIG.SHAPE_SIZE;
        const minX = margin;
        const maxX = CONFIG.WIDTH - margin;
        const minY = CONFIG.HEIGHT * 0.2; // Keep below title
        const maxY = CONFIG.HEIGHT - margin;
        const minDistance = CONFIG.SHAPE_SIZE * 1.5; // Minimum distance between shapes
        
        // Generate random target position that doesn't overlap
        let randomX, randomY, attempts = 0;
        let validPosition = false;
        
        while (!validPosition && attempts < 50) {
            randomX = minX + Math.random() * (maxX - minX);
            randomY = minY + Math.random() * (maxY - minY);
            validPosition = true;
            
            // Check distance from all targets (TS)
            for (const target of this.targets) {
                const dx = randomX - target.x();
                const dy = randomY - target.y();
                const distance = Math.sqrt(dx * dx + dy * dy);
                
                if (distance < minDistance) {
                    validPosition = false;
                    break;
                }
            }
            
            // Check distance from other draggables
            if (validPosition) {
                for (const other of this.draggables) {
                    if (other === draggable || other.isMatched) continue;
                    
                    const dx = randomX - other.x();
                    const dy = randomY - other.y();
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    
                    if (distance < minDistance) {
                        validPosition = false;
                        break;
                    }
                }
            }
            
            attempts++;
        }
        
        // If we couldn't find a valid position, use current position
        if (!validPosition) {
            randomX = draggable.x();
            randomY = draggable.y();
        }
        
        const duration = 3 + Math.random() * 2; // 3-5 seconds
        
        // Create animation using Konva.Tween to get a reference
        const anim = new Konva.Tween({
            node: draggable,
            x: randomX,
            y: randomY,
            duration: duration,
            easing: Konva.Easings.EaseInOut,
            onUpdate: () => {
                // Update number text position if it exists
                if (draggable.numberText) {
                    draggable.numberText.position(draggable.position());
                }
            },
            onFinish: () => {
                // Continue moving THIS shape only if not stopped
                if (!draggable.animationStopped && !draggable.isDragging() && !draggable.isMatched) {
                    this.animateSingle(draggable);
                }
            }
        });
        
        // Start the animation
        anim.play();
        
        // Store animation reference
        draggable.movementAnim = anim;
    }
    
    /**
     * Stop movement animation for a specific draggable.
     * @param {Konva.Node} draggable - Konva draggable shape
     */
    stop(draggable: CustomNode) {
        // Set flag to prevent restart FIRST
        draggable.animationStopped = true;
        
        if (draggable.movementAnim) {
            // Destroy the tween completely to stop it immediately
            draggable.movementAnim.destroy();
            // draggable.movementAnim = null;
        }
    }
    
    /**
     * Stop all movement animations.
     */
    stopAll() {
        this.draggables.forEach((draggable: CustomNode) => {
            this.stop(draggable);
        });
    }
    
    /**
     * Update method called by GameLoop each frame.
     * Currently a no-op since we're using Konva.Tween for animations.
     * This could be used in the future for frame-by-frame physics.
     * @param _deltaTime - Time elapsed since last frame (in milliseconds) - currently unused
     */
    update(_deltaTime: number): void {
        // No-op: Konva.Tween handles animation internally
        // This implementation allows MovementManager to be registered
        // with GameLoop without doing frame-by-frame updates
        // Future optimization: Could use _deltaTime for physics-based movement
    }
}
